import { cn } from '@/lib/utils';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface MagneticButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode;
  variant?: 'primary' | 'ghost';
}

/**
 * Primary CTA button — pulls slightly toward the cursor on hover ("magnetic"),
 * and pulses a soft gradient glow on press. Reused for every primary CTA.
 */
export function MagneticButton({ children, variant = 'primary', className, ...props }: MagneticButtonProps) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 200, damping: 15, mass: 0.4 });
  const springY = useSpring(y, { stiffness: 200, damping: 15, mass: 0.4 });

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = e.clientX - rect.left - rect.width / 2;
    const relY = e.clientY - rect.top - rect.height / 2;
    x.set(relX * 0.3);
    y.set(relY * 0.4);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.button
      style={{ x: springX, y: springY }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileTap={{ scale: 0.96 }}
      className={cn(
        'relative inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        variant === 'primary'
          ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-[0_0_40px_-8px_hsl(var(--primary)/0.6)] hover:shadow-[0_0_56px_-6px_hsl(var(--primary)/0.8)]'
          : 'border border-border/70 bg-card/40 text-foreground backdrop-blur-md hover:border-primary/50',
        className
      )}
      {...(props as any)}
    >
      {variant === 'primary' && (
        <motion.span
          className='absolute inset-0 rounded-full bg-gradient-to-r from-primary to-accent'
          initial={{ opacity: 0 }}
          whileTap={{ opacity: 0.5, scale: 1.3 }}
          transition={{ duration: 0.5 }}
          style={{ filter: 'blur(16px)' }}
        />
      )}
      <span className='relative z-10 flex items-center gap-2'>{children}</span>
    </motion.button>
  );
}

export default MagneticButton;
